import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from '../../ConfirmDialog';

/** A context, not props: the pager and the tab row are three levels from the card holding the typing. */
interface UnsavedWork {
  /** A card saying whether it is holding typing. Keyed, since a tab has more than one. */
  report: (key: string, dirty: boolean) => void;
  /** Runs the action, or asks first where something would be lost by it. */
  guard: (action: () => void) => void;
}

const noGuard: UnsavedWork = { report: () => {}, guard: action => action() };

const UnsavedWorkContext = createContext<UnsavedWork>(noGuard);

/** Outside a provider it just runs the action, so a card need not know whether its page cares. */
export const useUnsavedWork = (): UnsavedWork => useContext(UnsavedWorkContext);

/** A card reporting what it holds, for as long as it is mounted. */
export const useReportUnsaved = (key: string, dirty: boolean): void => {
  const { report } = useUnsavedWork();

  React.useEffect(() => {
    report(key, dirty);
    // Gone from the page is nothing to lose, so an unmounting card takes its claim with it.
    return () => report(key, false);
  }, [report, key, dirty]);
};

export const UnsavedWorkProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { t } = useTranslation();
  const [unsaved, setUnsaved] = useState<string[]>([]);
  // The thing the reader asked for, held while they answer for the typing it would cost.
  const pending = useRef<(() => void) | null>(null);
  const [asking, setAsking] = useState(false);

  const report = useCallback((key: string, dirty: boolean): void => {
    setUnsaved(current => {
      const held = current.includes(key);
      if (dirty === held) return current;
      return dirty ? [...current, key] : current.filter(each => each !== key);
    });
  }, []);

  const anyUnsaved = unsaved.length > 0;

  const guard = useCallback(
    (action: () => void): void => {
      if (!anyUnsaved) {
        action();
        return;
      }
      pending.current = action;
      setAsking(true);
    },
    [anyUnsaved],
  );

  const value = useMemo(() => ({ report, guard }), [report, guard]);

  return (
    <UnsavedWorkContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={asking}
        onOpenChange={next => {
          if (!next) {
            setAsking(false);
            pending.current = null;
          }
        }}
        title={t('budget.unsaved.title')}
        description={t('budget.unsaved.hint')}
        confirmLabel={t('budget.unsaved.leave')}
        onConfirm={() => {
          const action = pending.current;
          pending.current = null;
          setAsking(false);
          action?.();
        }}
      />
    </UnsavedWorkContext.Provider>
  );
};

export default UnsavedWorkProvider;
